import { Injectable } from '@angular/core';
import {
    CaseStatus,
    ClaimStatus,
    ClientEligibilityStatus,
    ClientInviteStatus,
    ClientProfileStatus,
    ClientProfileType,
    EvidenceStatus,
    SendGridTemplateType,
} from 'lemunzo-api';
import { PrimeNGTagSeverity } from '../enums/primeng-tag-severity';

/**
 * Represents the structure of a status service status model.
 */
export type StatusServiceStatusModel<T> = {
    label: string;
    value: T;
    name: string;
    primengTagSeverity?: PrimeNGTagSeverity;
};

@Injectable({
    providedIn: 'root',
})
export class StatusService {
    // TODO #CSD-1517: Add new statuses for claim status control.
    public readonly claimStatuses: StatusServiceStatusModel<number>[] = [
        {
            label: 'shared.statusService.worstClaim.inProgress',
            value: ClaimStatus.InProgress,
            name: 'InProgress',
            primengTagSeverity: PrimeNGTagSeverity.SECONDARY,
        },
        {
            label: 'shared.statusService.worstClaim.ocrPending',
            value: ClaimStatus.OCRPending,
            name: 'OcrPending',
            primengTagSeverity: PrimeNGTagSeverity.WARN,
        },
        {
            label: 'shared.statusService.worstClaim.pendingApproval',
            value: ClaimStatus.PendingApproval,
            name: 'PendingApproval',
            primengTagSeverity: PrimeNGTagSeverity.INFO,
        },
        {
            label: 'shared.statusService.worstClaim.approved',
            value: ClaimStatus.Approved,
            name: 'Approved',
            primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
        },
    ];

    public readonly clientProfileStatuses: StatusServiceStatusModel<number>[] =
        [
            {
                label: 'shared.statusService.clientProfile.pendingActivation',
                value: ClientProfileStatus.PendingActivation,
                name: 'PendingActivation',
                primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
            },
            {
                label: 'shared.statusService.clientProfile.actionRequired',
                value: ClientProfileStatus.ActionRequired,
                name: 'ActionRequired',
                primengTagSeverity: PrimeNGTagSeverity.DANGER,
            },
            {
                label: 'shared.statusService.clientProfile.requiredDataSubmitted',
                value: ClientProfileStatus.RequiredDataSubmitted,
                name: 'RequiredDataSubmitted',
                primengTagSeverity: PrimeNGTagSeverity.CONTRAST,
            },
            {
                label: 'shared.statusService.clientProfile.finalApprovalPending',
                value: ClientProfileStatus.FinalApprovalPending,
                name: 'FinalApprovalPending',
                primengTagSeverity: PrimeNGTagSeverity.WARN,
            },
            {
                label: 'shared.statusService.clientProfile.ready',
                value: ClientProfileStatus.Ready,
                name: 'Ready',
                primengTagSeverity: PrimeNGTagSeverity.INFO,
            },
            {
                label: 'shared.statusService.clientProfile.inactive',
                value: ClientProfileStatus.Inactive,
                name: 'Inactive',
                primengTagSeverity: PrimeNGTagSeverity.SECONDARY,
            },
        ];

    public readonly clientTypes: StatusServiceStatusModel<number>[] = [
        {
            label: 'shared.statusService.clientProfileType.person',
            value: ClientProfileType.Person,
            name: 'Person',
            primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
        },
        {
            label: 'shared.statusService.clientProfileType.organization',
            value: ClientProfileType.Organization,
            name: 'Organization',
            primengTagSeverity: PrimeNGTagSeverity.WARN,
        },
    ];

    // TODO CSD-745 Update based on BE changes; currently unused
    public readonly clientRegistrationStatuses: StatusServiceStatusModel<number>[] =
        [
            {
                label: 'shared.statusService.clientRegistration.undecided',
                value: 1,
                name: 'Undecided',
                primengTagSeverity: PrimeNGTagSeverity.WARN,
            },
            {
                label: 'shared.statusService.clientRegistration.rejected',
                value: 2,
                name: 'Rejected',
                primengTagSeverity: PrimeNGTagSeverity.DANGER,
            },
            {
                label: 'shared.statusService.clientRegistration.approved',
                value: 3,
                name: 'Approved',
                primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
            },
        ];

    public readonly clientInviteStatuses: StatusServiceStatusModel<number>[] = [
        {
            label: 'shared.statusService.clientInvite.inviteCreated',
            value: ClientInviteStatus.Created,
            name: 'InviteCreated',
            primengTagSeverity: PrimeNGTagSeverity.DANGER,
        },
        {
            label: 'shared.statusService.clientInvite.emailSent',
            value: ClientInviteStatus.Sent,
            name: 'EmailSent',
            primengTagSeverity: PrimeNGTagSeverity.WARN,
        },
        {
            label: 'shared.statusService.clientInvite.registrationFinished',
            value: ClientInviteStatus.RegistrationFinished,
            name: 'RegistrationFinished',
            primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
        },
    ];

    public readonly caseStatuses: StatusServiceStatusModel<number>[] = [
        {
            label: 'shared.statusService.case.inactive',
            value: CaseStatus.Inactive,
            name: 'Inactive',
            primengTagSeverity: PrimeNGTagSeverity.DANGER,
        },
        {
            label: 'shared.statusService.case.registrationOnly',
            value: CaseStatus.RegistrationOnly,
            name: 'RegistrationOnly',
            primengTagSeverity: PrimeNGTagSeverity.WARN,
        },
        {
            label: 'shared.statusService.case.clientZoneOnly',
            value: CaseStatus.ClientZoneOnly,
            name: 'ClientZoneOnly',
            primengTagSeverity: PrimeNGTagSeverity.INFO,
        },
        {
            label: 'shared.statusService.case.fullyEnabled',
            value: CaseStatus.FullyEnabled,
            name: 'FullyEnabled',
            primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
        },
        {
            label: 'shared.statusService.case.archived',
            value: CaseStatus.Archived,
            name: 'Archived',
            primengTagSeverity: PrimeNGTagSeverity.CONTRAST,
        },
    ];

    public readonly evidenceStatuses: StatusServiceStatusModel<number>[] = [
        {
            label: 'shared.statusService.evidenceStatus.unprocessed',
            value: EvidenceStatus.Unprocessed,
            name: 'Unprocessed',
            primengTagSeverity: PrimeNGTagSeverity.WARN,
        },
        {
            label: 'shared.statusService.evidenceStatus.processed',
            value: EvidenceStatus.Processed,
            name: 'Processed',
            primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
        },
        {
            label: 'shared.statusService.evidenceStatus.invalid',
            value: EvidenceStatus.Invalid,
            name: 'Invalid',
            primengTagSeverity: PrimeNGTagSeverity.DANGER,
        },
    ];

    public readonly sendGridTemplateType: StatusServiceStatusModel<number>[] = [
        {
            label: 'shared.statusService.sendGridTemplate.newRegistration',
            value: SendGridTemplateType.NewRegistrationInvite,
            name: 'NewRegistrationInvite',
            primengTagSeverity: PrimeNGTagSeverity.WARN,
        },
        {
            label: 'shared.statusService.sendGridTemplate.manualInvite',
            value: SendGridTemplateType.ManualInvite,
            name: 'ManualInvite',
            primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
        },
        {
            label: 'shared.statusService.sendGridTemplate.registrationConfirmation',
            value: SendGridTemplateType.RegistrationConfirmation,
            name: 'RegistrationConfirmation',
            primengTagSeverity: PrimeNGTagSeverity.INFO,
        },
        {
            label: 'shared.statusService.sendGridTemplate.activationRequired',
            value: SendGridTemplateType.ActivationRequired,
            name: 'ActivationRequired',
            primengTagSeverity: PrimeNGTagSeverity.CONTRAST,
        },
    ];

    public readonly allEvidenceUploadedOptions: StatusServiceStatusModel<boolean>[] =
        [
            {
                label: 'shared.statusService.evidenceUpload.completed',
                value: true,
                name: 'Completed',
                primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
            },
            {
                label: 'shared.statusService.evidenceUpload.inProgress',
                value: false,
                name: 'InProgress',
                primengTagSeverity: PrimeNGTagSeverity.SECONDARY,
            },
        ];

    public readonly clientEligibilityStatuses: StatusServiceStatusModel<number>[] =
        [
            {
                label: 'az.clients.clientDetail.eligibility.pending',
                value: ClientEligibilityStatus.Pending,
                name: 'Pending',
                primengTagSeverity: PrimeNGTagSeverity.WARN,
            },
            {
                label: 'az.clients.clientDetail.eligibility.eligible',
                value: ClientEligibilityStatus.Eligible,
                name: 'Eligible',
                primengTagSeverity: PrimeNGTagSeverity.SUCCESS,
            },
            {
                label: 'az.clients.clientDetail.eligibility.notEligible',
                value: ClientEligibilityStatus.NotEligible,
                name: 'NotEligible',
                primengTagSeverity: PrimeNGTagSeverity.DANGER,
            },
        ];

    public claimStatusValueToLabel(value: number | undefined | null): string {
        return this.statusValueToLabel(value, this.claimStatuses);
    }

    public clientProfileStatusValueToLabel(
        value: number | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.clientProfileStatuses);
    }

    public clientTypeValueToLabel(value: number | undefined | null): string {
        return this.statusValueToLabel(value, this.clientTypes);
    }

    public clientRegistrationStatusValueToLabel(
        value: number | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.clientRegistrationStatuses);
    }

    public clientInviteStatusValueToLabel(
        value: number | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.clientInviteStatuses);
    }

    public caseStatusValueToLabel(value: number | undefined | null): string {
        return this.statusValueToLabel(value, this.caseStatuses);
    }

    public evidenceStatusValueToLabel(
        value: number | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.evidenceStatuses);
    }

    public sendGridTemplateValueToLabel(
        value: number | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.sendGridTemplateType);
    }

    public clientEligibilityStatusValueToLabel(
        value: number | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.clientEligibilityStatuses);
    }

    public allEvidenceUploadedValueToLabel(
        value: boolean | undefined | null
    ): string {
        return this.statusValueToLabel(value, this.allEvidenceUploadedOptions);
    }

    private statusValueToLabel<T>(
        value: T | undefined | null,
        statuses: StatusServiceStatusModel<T>[]
    ): string {
        if (value == null) {
            return 'shared.statusService.undefined';
        }

        const filtered = statuses.find((x) => x.value === value);

        if (filtered) {
            return filtered.label;
        } else {
            return 'shared.statusService.undefined';
        }
    }
}
